import { useState, useCallback } from 'react';
import Lightbox from '@/components/base/Lightbox';

interface LightboxImage {
  src: string;
  alt: string;
  caption?: string;
}

export default function CompanyStrengthSection() {
  const [lightboxImages, setLightboxImages] = useState<LightboxImage[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = useCallback((images: LightboxImage[], idx: number) => {
    setLightboxImages(images);
    setLightboxIndex(idx);
  }, []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev === 0 ? lightboxImages.length - 1 : prev - 1) : null));
  }, [lightboxImages.length]);
  const nextImage = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev === lightboxImages.length - 1 ? 0 : prev + 1) : null));
  }, [lightboxImages.length]);

  const visitImages: LightboxImage[] = [
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_1860-scaled.jpg',
      alt: 'Clientes internacionales visitando las instalaciones de Rixin Group en China',
      caption: 'Visita a Instalaciones Rixin Group',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_1894-scaled.jpg',
      alt: 'Reunión técnica con el equipo de Rixin Group en la planta de producción',
      caption: 'Reunión Técnica en Planta',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_1898-scaled.jpg',
      alt: 'Visita guiada a la línea de producción de demulsificantes en Rixin',
      caption: 'Línea de Producción Rixin',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_3539-1-scaled.jpg',
      alt: 'Clientes de Sudamérica en reunión de negocios con Rixin Group',
      caption: 'Reunión de Negocios Sudamérica',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_3515-scaled.jpg',
      alt: 'Equipo técnico de Rixin explicando el proceso de fabricación a visitantes',
      caption: 'Explicación del Proceso de Fabricación',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_1862-scaled.jpg',
      alt: 'Demostración de producto en el laboratorio de calidad de Rixin Group',
      caption: 'Laboratorio de Calidad Rixin',
    },
  ];

  const southAmericaImages: LightboxImage[] = [
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_3542.jpg',
      alt: 'Clientes venezolanos visitando la planta productiva de Rixin Group',
      caption: 'Clientes Venezolanos en Rixin',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/%E5%A4%96%E5%8C%851.jpg',
      alt: 'Vista panorámica de las instalaciones de Rixin Group con visitantes',
      caption: 'Vista Panorámica de Instalaciones',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_1838-1024x576.jpg',
      alt: 'Clientes cooperativos de Venezuela en reunión con Rixin Group',
      caption: 'Cooperación Venezuela-Rixin',
    },
  ];

  const southAmericaSmallImages: LightboxImage[] = [
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_9047-768x1024.jpg',
      alt: 'Clientes de Sudamérica en recepción de Rixin Group',
      caption: 'Recepción Rixin Group',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_4605-768x1024.jpg',
      alt: 'Reunión de negocios con clientes latinoamericanos',
      caption: 'Negocios Latinoamérica',
    },
  ];

  const moreMoments: LightboxImage[] = [
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_9129-1-scaled.jpg',
      alt: 'Equipo de Rixin Group presentando soluciones químicas a clientes internacionales',
      caption: 'Presentación de Soluciones Químicas',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_9152-1-scaled.jpg',
      alt: 'Reunión estratégica con clientes de la industria petrolera en Rixin',
      caption: 'Reunión Estratégica Petrolera',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_9143-1-scaled.jpg',
      alt: 'Clientes recorriendo el área de almacenamiento de productos terminados',
      caption: 'Área de Almacenamiento',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_9130-1-scaled.jpg',
      alt: 'Presentación técnica del proceso de producción de demulsificantes',
      caption: 'Proceso de Producción',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_9137-1-scaled.jpg',
      alt: 'Visita al laboratorio de investigación y desarrollo de Rixin Group',
      caption: 'Laboratorio I+D Rixin',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/IMG_9140-scaled.jpg',
      alt: 'Discusión técnica sobre especificaciones de producto con clientes',
      caption: 'Especificaciones de Producto',
    },
  ];

  const visitVideos = [
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/%E5%85%AC%E5%8F%B8%E4%BA%BA%E5%83%8F%E4%BB%8B%E7%BB%8D.mp4',
      label: 'Conociendo a Rixin Group',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/%E7%AC%AC%E4%B8%80%E6%AC%A1%E5%8F%82%E8%A7%82%E7%94%9F%E4%BA%A7%E7%BA%BF.mp4',
      label: 'Recorrido por la Línea de Producción',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/%E4%B8%A4%E4%BA%BA%E5%8F%82%E8%A7%82.mp4',
      label: 'Visita a la Planta Productiva',
    },
    {
      src: 'https://rixingroup.cn/wp-content/uploads/2025/05/%E4%B8%A4%E4%BA%BA%E5%8F%82%E8%A7%822.mp4',
      label: 'Inspección de Calidad en Sitio',
    },
  ];

  return (
    <section id="company-strength" className="py-16 md:py-24 bg-background-50">
      <div className="px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 text-accent-900 text-xs font-semibold tracking-wide uppercase mb-4">
            <i className="ri-team-line w-3 h-3 flex items-center justify-center" />
            Visitas de Clientes Internacionales
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4">
            Clientes de Todo el Mundo Conocen a Rixin Group
          </h2>
          <p className="text-foreground-600 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
            Empresas petroleras de Venezuela, Ecuador, Sudamérica, África y Medio Oriente visitan las instalaciones de 
            <strong className="text-foreground-800"> Rixin Group en China </strong>
            para conocer de primera mano los procesos de producción, el sistema de calidad y la capacidad de I+D. 
            Estas son las mismas plantas donde se fabrica el demulsificante que RESINVENCA RESINAS DE VENEZUELA C.A. trae a Venezuela.
          </p>
        </div>

        {/* Cooperative Customers — Venezuela Highlight */}
        <div className="mb-12 md:mb-16 bg-background-100 rounded-2xl p-6 md:p-8 lg:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-flag-line text-primary-600 text-lg" />
            </div>
            <div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">Clientes de Venezuela en Rixin Group</h3>
              <p className="text-foreground-600 text-sm">Visitas de cooperación directa a la planta del fabricante</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {southAmericaImages.map((img, idx) => (
              <div
                key={img.src}
                onClick={() => openLightbox(southAmericaImages, idx)}
                className="group relative rounded-xl overflow-hidden bg-background-200 aspect-video cursor-pointer"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-foreground-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-zoom-in-line text-sm" />
                </div>
                <p className="absolute bottom-3 left-3 right-3 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {img.alt}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl p-5 md:p-6 border border-background-200">
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed italic">
              "Recientemente, un importante cliente cooperativo de <strong>Venezuela</strong> visitó nuestra empresa para conocer en profundidad 
              los procesos de producción, el flujo de productos y el sistema de control de calidad. Acompañado por el equipo directivo, 
              el cliente recorrió el taller de producción, el laboratorio y el sistema de almacenamiento, y elogió en el momento nuestra 
              fortaleza técnica, capacidad productiva y gestión de calidad, expresando una fuerte voluntad de cooperar con nosotros en el futuro."
            </p>
            <p className="text-foreground-500 text-xs mt-3 text-right">— Rixin Group, Reporte de Visita de Clientes</p>
          </div>
        </div>

        {/* Visit Videos */}
        <div className="mb-12 md:mb-16">
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">Videos de las Visitas a la Fábrica</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {visitVideos.map((video) => (
              <div key={video.src} className="bg-white rounded-xl overflow-hidden border border-background-200">
                <div className="relative aspect-video bg-background-200">
                  <video
                    src={video.src}
                    controls
                    preload="metadata"
                    className="w-full h-full object-cover"
                    controlsList="nodownload"
                  />
                </div>
                <div className="p-3">
                  <p className="text-foreground-800 text-sm font-medium text-center">{video.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Gallery — First Moments */}
        <div className="mb-12 md:mb-16">
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">Momentos en las Instalaciones de Rixin</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {visitImages.map((img, idx) => (
              <div
                key={img.src}
                onClick={() => openLightbox(visitImages, idx)}
                className={`group relative rounded-xl overflow-hidden bg-background-200 cursor-pointer ${
                  idx === 0 || idx === 3 ? 'aspect-[16/10]' : 'aspect-[4/3]'
                }`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-foreground-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-zoom-in-line text-sm" />
                </div>
                <p className="absolute bottom-3 left-3 right-3 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {img.alt}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Second Cooperative Customers Section — South America */}
        <div className="mb-12 md:mb-16 bg-accent-50 rounded-2xl p-6 md:p-8 lg:p-10 border border-accent-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-earth-line text-accent-600 text-lg" />
            </div>
            <div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">Clientes de Sudamérica Visitan Rixin</h3>
              <p className="text-foreground-600 text-sm">Cooperación estratégica entre continente y fabricante</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="grid grid-cols-2 gap-3">
              {southAmericaSmallImages.map((img, idx) => (
                <div
                  key={img.src}
                  onClick={() => openLightbox(southAmericaSmallImages, idx)}
                  className="group relative rounded-xl overflow-hidden cursor-pointer"
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-36 sm:h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-white/90 text-foreground-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-zoom-in-line text-xs" />
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-xl p-5 md:p-6 border border-accent-100 flex flex-col justify-center">
              <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
                Recientemente, un grupo de clientes cooperativos de <strong>Sudamérica</strong> llegó a nuestra empresa para realizar 
                intercambios sobre la capacidad de producción y la aplicación de productos químicos para campos petroleros. 
                Acompañados por el equipo de la empresa, los clientes visitaron el laboratorio de I+D, el taller de producción 
                y el área de almacenamiento de productos terminados.
              </p>
              <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
                Tuvieron un conocimiento profundo de las capacidades de nuestra empresa en <strong>personalización de productos, 
                control de calidad y servicios técnicos</strong>.
              </p>
            </div>
          </div>

          {/* South America Videos */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { src: 'https://rixingroup.cn/wp-content/uploads/2025/05/%E5%8D%97%E7%BE%8E1.mp4', label: 'Visita Suramérica 1' },
              { src: 'https://rixingroup.cn/wp-content/uploads/2025/05/%E5%8D%97%E7%BE%8E.mp4', label: 'Visita Suramérica 2' },
              { src: 'https://rixingroup.cn/wp-content/uploads/2025/05/%E5%8D%97%E7%BE%8E2.mp4', label: 'Visita Suramérica 3' },
              { src: 'https://rixingroup.cn/wp-content/uploads/2025/05/%E5%8D%97%E7%BE%8E3.mp4', label: 'Visita Suramérica 4' },
            ].map((video) => (
              <div key={video.src} className="bg-white rounded-xl overflow-hidden border border-accent-100">
                <div className="relative aspect-video bg-background-200">
                  <video
                    src={video.src}
                    controls
                    preload="metadata"
                    className="w-full h-full object-cover"
                    controlsList="nodownload"
                  />
                </div>
                <div className="p-2">
                  <p className="text-foreground-800 text-xs font-medium text-center">{video.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* More Moments Gallery */}
        <div className="mb-12">
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">Más Momentos de Cooperación</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {moreMoments.map((img, idx) => (
              <div
                key={img.src}
                onClick={() => openLightbox(moreMoments, idx)}
                className="group relative rounded-xl overflow-hidden bg-background-200 aspect-[4/3] cursor-pointer"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-foreground-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-zoom-in-line text-sm" />
                </div>
                <p className="absolute bottom-3 left-3 right-3 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {img.alt}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-background-100 rounded-2xl p-8 md:p-10">
          <p className="text-foreground-700 text-base md:text-lg max-w-2xl mx-auto mb-6 leading-relaxed">
            Estas son las mismas instalaciones donde se produce el <strong>demulsificante</strong> que 
            <strong> RESINVENCA RESINAS DE VENEZUELA C.A. </strong>importa y distribuye en Venezuela. 
            La calidad que ven estos clientes internacionales es la misma que llega a los campos petroleros venezolanos.
          </p>
          <a
            href="/rixin"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
          >
            Conocer Más sobre Rixin Group
            <i className="ri-arrow-right-line text-sm" />
          </a>
        </div>
      </div>

      {/* Lightbox */}
      <Lightbox
        images={lightboxImages}
        index={lightboxIndex}
        onClose={closeLightbox}
        onPrev={prevImage}
        onNext={nextImage}
      />
    </section>
  );
}